if (!customElements.get('delivery-options')) {
  customElements.define(
    'delivery-options',
    class DeliveryOptions extends HTMLElement {
      constructor() {
        super();
        this.refCache = {};
        this.onChange = this.onChange.bind(this);

        this.container = this.closest('quick-view-modal, .shopify-section') || this;
      }

      connectedCallback() {
        this.addEventListener('change', this.onChange);
        // Defer so the product form element is parsed and its listener is registered
        setTimeout(() => this.dispatchSubmitButtonUpdate(), 0);

        // Re-evaluate once the address is confirmed: the submit gate reads the address cookie,
        // which only exists after setAddressResultCookie() has run.
        this._onAddressConfirmed = () => {
          const selected = this.querySelector('input[name^="delivery-option"]:checked');
          if (selected?.value === 'delivery') {
            this.updateDeliveryOptions('delivery');
          } else {
            this.dispatchSubmitButtonUpdate();
          }
        };
        document.addEventListener('deliveryAddress:confirmed', this._onAddressConfirmed);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        document.removeEventListener('deliveryAddress:confirmed', this._onAddressConfirmed);
      }

      refs(value) {
        if (this.refCache[value]) return this.refCache[value];
        this.refCache[value] = this.querySelector(`[ref='${value}']`);
        return this.refCache[value];
      }

      get productForm() {
        this._productForm = this._productForm || this.closest('.product-form')?.querySelector("form[is='product-form'], form[ref='main-pdp-form']");
        return this._productForm;
      }

      get deliverySection() {
        return this.querySelector('[data-delivery-section]');
      }

      onChange(event) {
        const radio = event.target?.closest?.('input[type="radio"]');
        const select = event.target?.closest?.('select[ref="planSelect"]');

        if (select) {
          this.dispatchSubmitButtonUpdate();
          return;
        }

        if (!radio) return;

        // Names are section-scoped (frequency-option-{{ section.id }} / delivery-option-{{ section.id }})
        // so same-name radios in a different instance (e.g. quick-view open on top of a PDP) don't
        // share one native radio group with this instance's — match by prefix, not exact string.
        if (radio.name.startsWith('frequency-option')) {
          this.onFrequencyChange(radio.value);
        }
        else if (radio.name.startsWith('delivery-option')) {
          this.updateDeliveryOptions(radio.value);
        }

        this.dispatchSubmitButtonUpdate();
      }

      onFrequencyChange(freq) {
        this.dataset.frequency = freq;
        const autoorderPanel = this.querySelector('[data-autoorder-panel]');

        // Both one-time and autoorder unlock the delivery section
        this.deliverySection?.removeAttribute('data-disabled');

        if (autoorderPanel) autoorderPanel.style.display = freq === 'autoorder' ? '' : 'none';

        const widget = this.querySelector('selling-plan-widget');
        widget?.syncSellingPlanInputs?.(freq);
      }

      clearDeliveryOption() {
        const productForm = this.productForm;
        if (!productForm) return;
        productForm.querySelector('input[name="properties[_delivery_method]"]')?.remove();
      }

      updateDeliveryOptions(deliveryOption) {
        const productForm = this.productForm;
        if (!productForm) return;

        productForm.querySelector('input[name="properties[_delivery_method]"]')?.remove();

        const methodInput = document.createElement('input');
        methodInput.type = 'hidden';
        methodInput.name = 'properties[_delivery_method]';
        methodInput.value = deliveryOption;
        productForm.appendChild(methodInput);

        document.dispatchEvent(new CustomEvent('location:changed'));
        this.dispatchSubmitButtonUpdate();
      }

      _getLocationNameForMethod(method) {
        if (!method) return null;
        const cookies = document.cookie ? document.cookie.split('; ') : [];
        try {
          if (method === 'delivery') {
            const prefix = 'addressDrawerResult=';
            const cookie = cookies.find((e) => e.startsWith(prefix));
            if (!cookie) return null;
            const parsed = JSON.parse(decodeURIComponent(cookie.substring(prefix.length)));
            return parsed?.formatted_address || parsed?.name || null;
          }

          // pickup (and any other method) — read from locationDrawerSelected
          const prefix = 'locationDrawerSelected=';
          const cookie = cookies.find((e) => e.startsWith(prefix));
          if (!cookie) return null;
          const parsed = JSON.parse(decodeURIComponent(cookie.substring(prefix.length)));
          if (!parsed) return null;
          // Per-method format: { pickup: { lat, lng, name }, ... }
          if (parsed.latitude === undefined) return parsed[method]?.name || null;
          // Legacy flat format: { latitude, longitude, name }
          return parsed.name || null;
        } catch {
          return null;
        }
      }

      dispatchSubmitButtonUpdate() {
        const freq = this.querySelector('input[name^="frequency-option"]:checked')?.value;
        let enable = false;
        const deliveryMethodSelected = this.querySelector('input[name^="delivery-option"]:checked');
        const deliveryOptionWrapper = deliveryMethodSelected?.closest('.delivery-option');
        const outOfStock = !!deliveryOptionWrapper?.nextElementSibling?.querySelector('.out-of-stock');
        // Gated on the buyer having actually picked a store/address for the selected method.
        // This used to probe the `_pickup_at` hidden input as a proxy; that property is no longer
        // written (FPS-682), so read the same cookie the property was derived from instead.
        const locationSelected = !!this._getLocationNameForMethod(deliveryMethodSelected?.value);

        if (!freq) {
          // No selling plans on this product — use legacy rule (delivery option selected + no out-of-stock)
          const hasFrequencyRadios = !!this.querySelector('input[name^="frequency-option"]');
          if (!hasFrequencyRadios) {
            enable = !!deliveryMethodSelected && !outOfStock && !!locationSelected;
          }
        } else if (freq === 'onetime') {
          enable = !!deliveryMethodSelected && !outOfStock && !!locationSelected;
        } else if (freq === 'autoorder') {
          const sellingPlanInput = this.querySelector('input[name="selling_plan"]');
          const hasDelivery = !!deliveryMethodSelected && !!locationSelected;
          enable = !!(sellingPlanInput?.value) && hasDelivery && !outOfStock;
        }
        const evt = new CustomEvent('update:submitButton', {
          bubbles: true,
          detail: { enable },
        });

        this.dispatchEvent(evt);

        const productForm = this.productForm;
        if (!productForm) return;

        productForm.dispatchEvent(new CustomEvent('update:submitButton', { detail: { enable } }));
      }
    },
  );
}
